import React, { use, useCallback, useMemo, useState } from "react";
import { SvgProps } from "react-native-svg";
import { useAtom } from "jotai";
import { useMutation } from "@apollo/client/react";
import { currentLang } from "../atoms/i18n";
import { AVAILABLE_LANGUAGE } from "../i18n/config";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import { useApolloErrorSnackbar, useSnackbar } from "../hooks/snackbar";
import { useIntercom } from "../hooks/intercom";
import { AuthManagerContext } from "../contexts/AuthManager";
import { UPDATE_USER } from "../graphql/identity";
import { SelectChooser, SelectItemType } from "./select/SelectChooser";
import ListItem from "../ui_atoms/ListItem";
import Button from "./Button";
import FranceFlag from "../assets/icons/france-flag.svg";
import GermanyFlag from "../assets/icons/germany-flag.svg";

const FLAG_SIZE = 24;

// Circular flags rendered as the list item's left icon. Kept as SVG so they display
// consistently across iOS and Android (emoji flags are not rendered on stock Android).
const FLAGS: Record<string, React.FC<SvgProps>> = {
    fr: FranceFlag,
    de: GermanyFlag,
};

/**
 * "Changer de langue" entry for the profile screen. Renders a CTA row that opens a
 * SelectChooser listing the available languages, and persists the chosen language on the
 * user profile so the preference survives across sessions and devices.
 */
const LanguageSwitcher = () => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const [lang, setLang] = useAtom(currentLang);
    const { updateUser: updateIntercomUser } = useIntercom();
    const { refreshUser } = use(AuthManagerContext);
    const { showSnackbar } = useSnackbar();
    const [isVisible, setIsVisible] = useState(false);

    const [updateUser, { error, reset }] = useMutation(UPDATE_USER);
    useApolloErrorSnackbar(error, reset, i18n.t("language.update_error"));

    const open = useCallback(() => setIsVisible(true), []);
    const onDismiss = useCallback(() => setIsVisible(false), []);

    const items = useMemo<SelectItemType<string>[]>(
        () =>
            AVAILABLE_LANGUAGE.map((code) => {
                const Flag = FLAGS[code];
                return {
                    value: code,
                    label: i18n.t(`language.name.${code}`),
                    iconComponent: Flag ? <Flag width={FLAG_SIZE} height={FLAG_SIZE} /> : undefined,
                };
            }),
        [i18n],
    );

    const onSelect = useCallback(
        async ({ value }: SelectItemType<string>) => {
            if (value === lang) {
                return;
            }
            // Switch the UI immediately, then persist on the backend.
            setLang(value);
            // The backend update only changes the Intercom contact server-side; the SDK
            // session on the device keeps its cached language unless told directly.
            void updateIntercomUser({ languageOverride: value }).catch(() => {});
            try {
                await updateUser({ variables: { userData: { lang: value } } });
                await refreshUser?.();
                // Resolve the confirmation in the freshly selected language regardless of the
                // locale captured by this closure.
                showSnackbar({
                    mode: "success",
                    text: i18n.t("language.service_changed", { locale: value }),
                });
            } catch {
                // Failure is surfaced through useApolloErrorSnackbar.
            }
        },
        [i18n, lang, refreshUser, setLang, showSnackbar, updateIntercomUser, updateUser],
    );

    if (AVAILABLE_LANGUAGE.length < 2) {
        return null;
    }

    return (
        <>
            <ListItem
                labels={[{ text: i18n.t("language.change"), mode: "cta" }]}
                rightIcon="language"
                rightActionMode="medium"
                rightIconColor={colors.secondary}
                onPress={open}
            />
            <SelectChooser
                visible={isVisible}
                onDismiss={onDismiss}
                lightMode
                selectedValue={lang}
                searchItems={items}
                onSelect={onSelect}
                bottomComponent={
                    <Button label={i18n.t("close")} buttonMode="outlined" onPress={onDismiss} />
                }
            />
        </>
    );
};

export default LanguageSwitcher;
